import { Package, ShoppingCart, ExternalLink, Heart, CheckCircle, Clock } from 'lucide-react';
import { ProductWithAvailability, Campaign } from '../../hooks/useCampaignDetail';
import { isExpired } from '../../utils/date';

interface Props {
  products: ProductWithAvailability[];
  campaign: Campaign;
  isOwner: boolean;
  onDonateProduct: (productId: string, maxQuantity: number) => void;
}

export function ProductList({ products, campaign, isOwner, onDonateProduct }: Props) {
  const expired = isExpired(campaign.deadline);

  // Overall progress
  const totalNeeded = products.reduce((sum, p) => sum + p.totalNeeded, 0);
  const totalCommitted = products.reduce((sum, p) => sum + p.committedQuantity, 0);
  const overallPercentage = totalNeeded > 0 ? Math.round((totalCommitted / totalNeeded) * 100) : 0;
  const completedCount = products.filter(p => p.isFullyCommitted).length;

  return (
    <div className="rounded-xl border-2 border-indigo-100 bg-gradient-to-b from-indigo-50/50 to-white p-5 sm:p-6 mb-6">
      {/* Section header with global progress */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
            <ShoppingCart className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Productos Necesarios</h2>
            <p className="text-xs text-gray-500">{completedCount} de {products.length} completados</p>
          </div>
        </div>

        {products.length > 0 && (
          <div className="flex items-center space-x-3">
            <div className="w-32 sm:w-40 bg-gray-200 rounded-full h-2.5">
              <div
                className="h-full rounded-full bg-indigo-600 transition-all duration-700"
                style={{ width: `${Math.min(overallPercentage, 100)}%` }}
              />
            </div>
            <span className="text-sm font-bold text-indigo-600">{overallPercentage}%</span>
          </div>
        )}
      </div>

      {products.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-lg border border-dashed border-gray-300">
          <Package className="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p className="text-gray-500">No hay productos definidos para esta campaña.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {products.map((product) => {
            const canDonate = !expired && !product.isFullyCommitted && !isOwner && campaign.status === 'active';
            return (
              <div
                key={product.id}
                className={`bg-white border rounded-xl p-4 transition-all ${
                  product.isFullyCommitted
                    ? 'border-green-200 bg-green-50/30'
                    : canDonate
                    ? 'border-gray-200 hover:border-indigo-300 hover:shadow-md cursor-pointer'
                    : 'border-gray-200'
                }`}
                onClick={() => canDonate && onDonateProduct(product.id, product.availableQuantity)}
              >
                {/* Product name + reference */}
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 text-sm leading-tight">{product.name}</h3>
                    {product.description && (
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{product.description}</p>
                    )}
                  </div>
                  {product.referenceLink && (
                    <a
                      href={product.referenceLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-500 hover:text-indigo-700 flex-shrink-0 ml-2"
                      onClick={(e) => e.stopPropagation()}
                      title="Ver referencia"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>

                {/* Progress bar */}
                <div className="mb-3">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        product.isFullyCommitted ? 'bg-green-500' : 'bg-indigo-500'
                      }`}
                      style={{ width: `${Math.min(product.commitmentPercentage, 100)}%` }}
                    />
                  </div>
                </div>

                {/* Stats row */}
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-600">
                    <span className="font-bold text-gray-900">{product.committedQuantity}</span>
                    <span className="text-gray-400"> / </span>
                    <span className="font-bold text-indigo-600">{product.totalNeeded}</span>
                    <span className="ml-1">{product.unit}</span>
                    {!product.isFullyCommitted && (
                      <span className="ml-2 text-amber-600 font-medium">
                        · Faltan {product.availableQuantity} {product.unit}
                      </span>
                    )}
                  </div>

                  {product.isFullyCommitted ? (
                    <span className="text-xs font-medium text-green-700 flex items-center">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Completado
                    </span>
                  ) : canDonate ? (
                    <button className="bg-indigo-600 text-white px-2.5 py-1 rounded-md text-xs font-medium hover:bg-indigo-700 transition-colors flex items-center space-x-1"
                      onClick={(e) => { e.stopPropagation(); onDonateProduct(product.id, product.availableQuantity); }}>
                      <Heart className="w-3 h-3" />
                      <span>{campaign.campaignType === 'solidarity' ? 'Aportar' : 'Regalar'}</span>
                    </button>
                  ) : (
                    <span className="text-xs text-amber-700 flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      Faltan {product.availableQuantity}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
